import {
  ArrowRight,
  ClockCounterClockwise,
  Copy,
  DotsThreeVertical,
  PauseCircle,
  PencilSimpleLine,
  PlayCircle,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { Campaign } from "common/types";

import useCampaignValidation from "../../hooks/useCampaignValidation";
import CampaignActionsDialog from "./CampaignActionsDialog";

import type { CampaignActionType, DetailedCampaign } from "../../types";

interface CampaignActionsDropdownProps {
  campaign: Campaign | DetailedCampaign;
  isDetailedView?: boolean;
}

export default function CampaignActionsDropdown({
  campaign,
  isDetailedView = false,
}: CampaignActionsDropdownProps) {
  const navigate = useNavigate();
  const isDesktop = useTwBreakpoint("lg");
  const { accounts_count } = useSelectedWorkspaceContext();

  const { state, id, owners } = campaign;
  const { hasInsufficientLicenses } = useCampaignValidation(owners);

  const [isOpen, setIsOpen] = useState(false);
  const [actionType, setActionType] = useState<CampaignActionType>();

  const isDuplicateDisabled = accounts_count === 0;

  function setAction(action: CampaignActionType) {
    setActionType(action);
    setIsOpen(true);
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            intent="iconOnly"
            variant="secondary-black"
            size={isDesktop ? "sm" : "md"}
          >
            <DotsThreeVertical />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent size={isDesktop ? "sm" : "md"} align="end">
          <DropdownMenuList>
            {isDetailedView ? (
              <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
                {(isDisabled) => (
                  <DropdownMenuItem
                    onClick={() => setAction("editName")}
                    disabled={isDisabled}
                  >
                    <PencilSimpleLine />
                    Edit name
                  </DropdownMenuItem>
                )}
              </RestrictedComponent>
            ) : (
              <DropdownMenuItem onClick={() => navigate(id)}>
                <ArrowRight />
                Go to details
              </DropdownMenuItem>
            )}
            <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
              <DropdownMenuItem
                onClick={() => setAction("duplicate")}
                disabled={isDuplicateDisabled}
              >
                <Copy />
                Duplicate campaign
              </DropdownMenuItem>
              <RenderIf
                condition={
                  state === "CREATED" ||
                  state === "PAUSED" ||
                  state === "SCHEDULED"
                }
              >
                <DropdownMenuItem
                  onClick={() =>
                    setAction(hasInsufficientLicenses ? "license" : "start")
                  }
                >
                  <PlayCircle />
                  Start campaign
                </DropdownMenuItem>
              </RenderIf>
              <RenderIf condition={state === "ACTIVE"}>
                <DropdownMenuItem onClick={() => setAction("pause")}>
                  <PauseCircle />
                  Pause campaign
                </DropdownMenuItem>
              </RenderIf>
              <RenderIf condition={state === "CREATED"}>
                <DropdownMenuItem
                  onClick={() =>
                    setAction(hasInsufficientLicenses ? "license" : "schedule")
                  }
                >
                  <ClockCounterClockwise />
                  Schedule campaign
                </DropdownMenuItem>
              </RenderIf>
              <RenderIf condition={state === "SCHEDULED"}>
                <DropdownMenuItem onClick={() => setAction("unschedule")}>
                  <ClockCounterClockwise />
                  Remove schedule
                </DropdownMenuItem>
              </RenderIf>
              <DropdownMenuItem
                variant="danger"
                onClick={() => setAction("delete")}
              >
                <Trash />
                Delete campaign
              </DropdownMenuItem>
            </RestrictedComponent>
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>

      {!!actionType && (
        <CampaignActionsDialog
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          actionType={actionType}
          campaign={campaign}
        />
      )}
    </>
  );
}
